import * as React from "react";
import styled, { css } from "styled-components";
import _ from "lodash";
import { useDispatch } from "redux-react-hook";
import { lighten } from "polished";
import { useLocation } from "react-router-dom";

import { breakpoint } from "src/library/utils/styled-media-queries/index";
import { Icon } from "src/library/components/Icon";
import useMenuItems from "src/core/shared/hooks/menu/useMenuItems";
import { MenuContext } from "src/core/shared/hooks/menu/useMenu";
import { Logo } from "src/core/shared/components/Logo";
import { HeaderContext } from "src/core/shared/components/HeaderContext";
import useCanAccessRoute from "src/core/shared/hooks/menu/useCanAccessRoute";
import DropSwitcher from "src/core/drops/components/DropSwitcherButton";
import { Icons } from "src/library/components/Icon/types";
import { useNotificationBannerContext } from "src/core/shared/context/NotificationBanner/NotificationBannerContext";

import useCurrentUser from "../../core/users/hooks/useCurrentUser";
import { footerItems } from "./footer-items";
import FooterMenuItem from "./components/FooterMenuItem";
import MainMenuItem from "./components/MainMenuItem";
import {
  ContextHeight,
  MobileMenuHeight
} from "./utils";
import { ContextTitle } from "./components/ContextTitle";

export const SidebarContext = React.createContext(null);

export const SidebarProvider: React.ReactFCWithChildren = ({ children }) => {
  const [ mobileMenuVisible, setMobileMenuVisible ] = React.useState<boolean>(false);
  const dispatch = useDispatch();
  const menuItems = useMenuItems();
  const [ menuState ] = React.useContext(MenuContext);
  const location = useLocation();
  const canAccessRoute = useCanAccessRoute();
  const [ { currentRole } ] = useCurrentUser();
  const { banners } = useNotificationBannerContext();
  const bannersHeight = banners.length * 55;

  React.useEffect(() => {
    setMobileMenuVisible(false);
  }, [ location.pathname ]);

  const currentContext = React.useMemo(() => {
    return menuState.context[ 0 ];
  }, [ menuState.context ]);

  const showSidebar = React.useMemo(() => {
    if (menuState.currentRoute?.menuType === "NONE") {
      return false;
    }

    return true;
  }, [ menuState.currentRoute ]);

  return (
    <SidebarContext.Provider value={null}>
      {showSidebar && (
        <SideBarStyled bannersHeight={bannersHeight}>
          {/* Mobile menu header */}

          <div className={`mobile-menu-header ${currentContext ? "in-context" : ""}`}>
            {currentContext ? (
              <ContextTitle currentContext={currentContext} />
            ) : (
              <div className={`logo-wrapper ${currentContext ? "in-context" : ""}`}>
                <Logo type="light" />
              </div>
            )}

            {currentContext ? (
              <Icon
                icon={mobileMenuVisible ? Icons.CHEVRONUP : Icons.CHEVRONDOWN}
                $fillColor="white"
                size={30}
                onClick={() => setMobileMenuVisible(!mobileMenuVisible)}
              />
            ) : (
              <Icon
                icon={mobileMenuVisible ? Icons.CLOSE : Icons.BURGERMENU}
                $fillColor="white"
                size={30}
                onClick={() => setMobileMenuVisible(!mobileMenuVisible)}
              />
            )}
          </div>

          {/* main menu */}

          <div className={`main-menu ${mobileMenuVisible && "mobile-menu-open"}`}>
            <div className="menu-left">
              {_.map(
                _.filter(footerItems(dispatch), route => canAccessRoute(route)),
                (route, i) => (
                  <FooterMenuItem
                    key={`${route.path}-${i}`}
                    route={route}
                  />
                )
              )}
            </div>

            <div className="menu-right">
              <div className={`logo-wrapper ${currentContext ? "in-context" : ""}`}>
                <Logo type="light" />
              </div>

              {currentRole?.type === "customer" && !currentContext && <DropSwitcher />}

              {currentContext && (
                <HeaderContext
                  isMobileMenu
                  context={currentContext}
                />
              )}

              {currentContext && (
                <ContextTitle currentContext={currentContext} />
              )}

              {menuState.currentRoute?.parent && (
                <MainMenuItem
                  route={menuState.currentRoute?.parent}
                  isParent
                />
              )}

              {_.map(menuItems, route => (
                <MainMenuItem
                  key={route?.title?.replace(/\s/g, "_") || route.path}
                  route={route}
                />
              ))}
            </div>
          </div>
        </SideBarStyled>
      )}

      {children}
    </SidebarContext.Provider>
  );
};

const SideBarStyled = styled.div<{ bannersHeight?: number }>`
  ${({ theme, bannersHeight }) => css`
    background: ${theme.overrides?.menu?.background ? theme.overrides.menu.background : theme.colors.primaryLight};
    min-width: 100vw;
    display: flex;
    flex-direction: column;
    position: relative;
    height: 100vh;
    
    .main-menu {
      display: none;

      &.mobile-menu-open {
        position: fixed;
        top: ${MobileMenuHeight}px;
        z-index: 100;
        left: 0;
        width: 100vw;
        height: calc(100vh - ${MobileMenuHeight}px);
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        background: ${lighten(0.05, theme.colors.primaryLight)};
      }
    }

    .mobile-menu-header {
      display: flex;
      flex-direction: row;
      height: ${ContextHeight}px;
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      background: ${lighten(0.05, theme.colors.primaryLight)};
      z-index: 100;
      align-items: center;
      justify-content: space-between;
      padding: ${theme.spacing.padding.xs} ${theme.spacing.padding.md};

      &.in-context {
        background: ${theme.overrides?.contextMenu?.background ? theme.overrides.contextMenu.background : lighten(0.05, theme.colors.primaryLight)};
      }

      .logo-wrapper {
        height: 100%;
        padding: 15px;

        img {
          max-width: 100%;
        }
      }
    }

    .menu-left {
      width: 100%;
      display: flex;
      flex-direction: row;
      background: ${theme.overrides?.menu?.footerBackground ? theme.overrides.menu.footerBackground : theme.colors.primaryDark};
      order: 2;
      position: sticky;
      bottom: 0;
    }

    .menu-right {
      padding: ${theme.spacing.padding.sm} 0;
      display: flex;
      flex-direction: column;
      order: 1;
      overflow-y: scroll;

      .context-exit {
        display: none;
      }

      .logo-wrapper {
        display: none;
      }
    }

    .context-exit {
      display: flex;
      align-items: center;
      padding: ${theme.spacing.padding.md};

      ${theme.overrides?.menu?.footerIconColor ? css`
      svg {
        fill: ${theme.overrides.menu.footerIconColor} !important;
      }
      ` : ""}
      
      ${theme.overrides?.menu?.footerBackground ? css`
      border-top: 1px solid ${theme.overrides.menu.footerBackground} !important;
      border-bottom: 1px solid ${theme.overrides.menu.footerBackground} !important;
      ` : ""}

      svg {
        margin-right: ${theme.spacing.margins.sm};
      }

      &:hover {
        cursor: pointer;

        span {
          font-weight: ${theme.fonts.weights.bold};
        }

        svg {
          fill: ${theme.colors.accentLight};
        }
      }
    }

    ${breakpoint("md", theme)} {
      flex-direction: row;
      min-width: 300px;
      height: 100vh;

      .header-context {
        display: none;
      }

      .mobile-menu-header {
        display: none;
      }

      .main-menu {
        display: inherit;
        width: 100%;
        display: flex;
        
        // reset the mobile menu if desktop dimensions
        &.mobile-menu-open {
          position: initial;
          top: initial;
          z-index: initial;
          left: initial;
          width: 100%;
          height: 100%;
          flex-direction: inherit;
          justify-content: inherit;
          background: inherit;
        }
      }

      .menu-left {
        width: 60px;
        height: calc(100vh - ${bannersHeight}px);
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
        align-items: center;
        background: ${theme.overrides?.menu?.footerBackground ? theme.overrides.menu.footerBackground : theme.colors.primaryDark};
        order: 1;
      }

      .menu-right {
        order: 2;
        flex-grow: 1;
        overflow-y: scroll;
        padding: 0;

        .context-exit {
          display: flex;
          border-bottom: 1px solid ${theme.colors.white};
        }

        .logo-wrapper {
          display: flex;
          align-items: center;
          height: 70px;
          padding: 10px 25px 10px 15px;
          margin-left: 0;

          &.in-context {
            background: ${theme.overrides?.contextMenu?.background ? theme.overrides.contextMenu.background : lighten(0.05, theme.colors.primaryLight)};
          }

          img {
            max-width: 100%;
            width: initial;
          }
        }
      }
    }
  `}
`;
